
import React, { useEffect, useState } from "react";
import { Loader2 } from "lucide-react";
import { supabase } from "@/integrations/supabase/client";
import { fetchSwingTendencies, fetchDriverTestResults } from '@/utils/puttingResults/fetchUtils';
import { SwingTendency } from '@/components/putting/training/charts/swing/types';
import DriverHistorySection from "@/components/putting/training/driver/DriverHistorySection";
import DriverTrainingHeader from "@/components/putting/training/driver/DriverTrainingHeader";
import DriverChartsSection from "@/components/putting/training/driver/DriverChartsSection";
import { enhanceTendenciesWithPoints, deleteDriverTendency, removeDriverDuplicates } from "@/utils/driver/driverUtils";
import { toast } from "sonner";
import NavigationMenu from "@/components/layout/NavigationMenu";
import { useTranslation } from "react-i18next";

// Define a common interface for driver test data
interface DriverTestResult {
  id: string;
  user_id: string;
  test_date: string;
  test_id: string;
  test_level?: string | null;
  points?: number;
  level?: string;
  tendency?: string;
  club_type?: string;
  distance?: string;
}

const DriverTraining = () => {
  const { t } = useTranslation('training');
  const [tendencies, setTendencies] = useState<SwingTendency[]>([]);
  const [driverTests, setDriverTests] = useState<DriverTestResult[]>([]);
  const [loading, setLoading] = useState(true);
  const [selectedLevel, setSelectedLevel] = useState<string | null>(null);
  const [refreshKey, setRefreshKey] = useState(0); // Add refresh key to force re-fetching

  // Fetch tendencies from the database
  useEffect(() => {
    const fetchData = async () => {
      try {
        setLoading(true);
        
        // Get the current user
        const { data: { session } } = await supabase.auth.getSession();
        if (!session?.user) {
          console.error("No authenticated user found");
          setLoading(false);
          return;
        }
        
        // Fetch swing tendencies
        const tendenciesData = await fetchSwingTendencies(session.user.id);
        console.log("Fetched swing tendencies:", tendenciesData.length);
        
        // Fetch driver tests from level_tests_results table
        const driverTestsData = await fetchDriverTestResults(session.user.id);
        console.log("Fetched driver tests:", driverTestsData.length);
        
        // Add points values to tendencies that don't have them
        const enhancedTendencies = enhanceTendenciesWithPoints(tendenciesData);
        
        // Process driver test results to add tendency if missing
        const processedDriverTests = driverTestsData.map(test => ({
          ...test,
          // Add required fields for compatibility with existing code
          club_type: 'driver',
          tendency: test.tendency || 'center', // Default tendency if not provided
          distance: 'accuracy',
          test_level: test.test_level || test.level || '3' // Map level field to test_level for consistency and default to 3
        }));
        
        setTendencies(enhancedTendencies);
        setDriverTests(processedDriverTests);
      } catch (error) {
        console.error("Error fetching driver data:", error);
        toast.error(t('driver.errorLoading'));
      } finally {
        setLoading(false);
      }
    };
    
    fetchData();
  }, [refreshKey]); // Add refreshKey dependency to trigger re-fetch

  // Handle level change from the history tabs
  const handleLevelChange = (level: string | null) => {
    console.log("Level changed to:", level);
    setSelectedLevel(level);
  };

  // Handle deleting a tendency
  const handleDeleteTendency = async (id: string) => {
    try {
      console.log("Deleting tendency with ID:", id);
      
      const result = await deleteDriverTendency(id);
      
      if (!result.success) {
        console.error("Delete operation failed:", result.error);
        toast.error(t('driver.deleteError'));
        return;
      }
      
      console.log("Delete operation successful");
      toast.success(t('driver.deleteSuccess'));
      
      // Force a re-fetch of data after deletion
      setRefreshKey(prev => prev + 1);
    } catch (err) {
      console.error("Exception during delete:", err);
      toast.error(t('driver.deleteError'));
    }
  };

  // Combine both data sources for charts and history
  const combinedData = [
    ...tendencies,
    ...driverTests.map(test => ({
      id: test.id,
      user_id: test.user_id,
      test_id: test.test_id,
      test_date: test.test_date,
      test_level: test.test_level || '3', // Force a level, default to 3
      club_type: 'driver',
      distance: 'accuracy',
      tendency: test.tendency || 'center',
      points: test.points || 0,
      created_at: test.test_date
    } as SwingTendency))
  ];
  
  // Apply duplicate removal function to filter out unwanted duplicate standard entries
  const filteredData = removeDriverDuplicates(combinedData);

  // Debug the current state
  useEffect(() => {
    console.log("Current selected level:", selectedLevel);
    console.log("Combined data count (before filtering):", combinedData.length);
    console.log("Filtered data count (after removing duplicates):", filteredData.length);
    console.log("Filtered data by level:", filteredData.filter(t => 
      selectedLevel === null || t.test_level === selectedLevel
    ).length);
  }, [selectedLevel, combinedData, filteredData]);

  return (
    <div className="min-h-screen bg-gradient-to-b from-golf-50 to-white">
      <NavigationMenu />
      <div className="container mx-auto py-8">
        <DriverTrainingHeader />

        {loading ? (
          <div className="p-8 flex justify-center">
            <Loader2 className="h-8 w-8 text-green-600 animate-spin" />
          </div>
        ) : (
          <>
            <DriverChartsSection 
              tendencies={filteredData}
              selectedLevel={selectedLevel}
            />
            
            <DriverHistorySection 
              tendencies={filteredData}
              onDeleteTendency={handleDeleteTendency}
              onLevelChange={handleLevelChange}
            />
          </>
        )}
      </div>
    </div>
  );
};

export default DriverTraining;
